import { Menu, Text, Title } from "@mantine/core";
import { TrashIcon } from "lucide-react";
import React, { useEffect } from "react";
import { ContextMenuState, SelectedKeys } from "./constants/types";
import { initialContextMenu } from "./constants";

interface ContextMenuProps {
  undoStack: any[];
  setUndoStack: React.Dispatch<React.SetStateAction<any[]>>;
  data: any;
  setData: React.Dispatch<React.SetStateAction<any>>;
  text: any;
  setText: React.Dispatch<React.SetStateAction<any>>;
  contextMenu: ContextMenuState;
  setContextMenu: React.Dispatch<React.SetStateAction<any>>;
  editMode: boolean;
  selectedFanZones: Record<string, any>;
  selectedDots: Record<string, any>;
  setSelectedDots: React.Dispatch<React.SetStateAction<Record<string, any>>>;
  selectedTextFigures: Record<string, any>;
  setSelectedTextFigures: React.Dispatch<
    React.SetStateAction<Record<string, any>>
  >;
  t: (key: string) => string;
  closeContextMenu: () => void;
  deSelectAll: () => void;
  selectedKeys: SelectedKeys;
}

const ContextMenu: React.FC<ContextMenuProps> = ({
  undoStack,
  setUndoStack,
  data,
  setData,
  text,
  setText,
  contextMenu,
  setContextMenu,
  editMode,
  selectedDots,
  setSelectedDots,
  selectedTextFigures,
  setSelectedTextFigures,
  t,
  closeContextMenu,
  deSelectAll,
  selectedKeys,
  selectedFanZones,
}: ContextMenuProps) => {
  useEffect(() => {
    const handleContextMenu = (event: MouseEvent) => {
      event.preventDefault();

      setContextMenu({
        visible: true,
        x: event.clientX,
        y: event.clientY,
      });
    };

    window.addEventListener("contextmenu", handleContextMenu);

    return () => {
      window.removeEventListener("contextmenu", handleContextMenu);
    };
  }, [setContextMenu]);

  if (!editMode || !contextMenu.visible) return null;

  const moveElement = (key: string, toFront: boolean) => {
    const index = Number(key);

    if (index < 0 || index >= text.length) return;

    const selectedElement = text[index];
    const updatedFigures = [...text];

    updatedFigures.splice(index, 1);

    if (toFront) {
      updatedFigures.push(selectedElement);
    } else {
      updatedFigures.unshift(selectedElement);
    }

    if (JSON.stringify(updatedFigures) !== JSON.stringify(text)) {
      setData((prevData: any) => ({
        ...prevData,
        properties: {
          ...prevData.properties,
          text: updatedFigures,
        },
      }));

      setText(updatedFigures);
      setSelectedTextFigures({});
    }
  };

  const moveSelectedElements = (toFront: boolean) => {
    const keysToMove = Object.values(selectedKeys);

    keysToMove.forEach((key) => {
      if (key) moveElement(key, toFront);
    });
  };

  const handleForeground = () => {
    moveSelectedElements(true);
  };

  const handleBackground = () => {
    moveSelectedElements(false);
  };

  const handleDeleteSelectedElements = () => {
    setUndoStack([...undoStack, data]);
    const updatedData = JSON.parse(JSON.stringify(data));

    // 1. Handle deletion of seats from `selectedDots`
    if (selectedDots && selectedDots.length > 0) {
      Object.keys(updatedData.data).forEach((key) => {
        if (selectedDots.includes(key)) {
          delete updatedData.data[key];
        }
      });
    }

    // 2. Handle deletion of text, rect or polyline from `selectedTextFigures`
    if (selectedTextFigures.length > 0) {
      const indicesToRemove = selectedTextFigures
        .map((key: { split: (arg0: string) => [any, any] }) => {
          const [type, index] = key.split(":");
          return { type, index: Number(index) };
        })
        .filter(({ index }: any) => !isNaN(index))
        .sort(
          (a: { index: number }, b: { index: number }) => b.index - a.index
        );

      indicesToRemove.forEach(({ index }: any) => {
        if (index >= 0 && index < updatedData.properties.text.length) {
          updatedData.properties.text.splice(index, 1);
        }
      });
    }

    if (selectedFanZones && Object.keys(selectedFanZones).length > 0) {
      const fanZonesData = updatedData.hall_parts_data || [];

      selectedFanZones.forEach((key: string) => {
        const index = key.split(":")[1]?.split("-")[1];

        if (index && !isNaN(Number(index)) && fanZonesData[Number(index)]) {
          delete fanZonesData[Number(index)];
        }
      });

      updatedData.hall_parts_data = fanZonesData.filter(
        (zone: {}) => Object.keys(zone).length > 0
      );
    }

    setData(updatedData);
    setText(updatedData.properties.text);
    setSelectedDots([]);
    setSelectedTextFigures([]);
    setContextMenu(initialContextMenu);
  };

  return (
    <Menu
      opened={contextMenu.visible}
      onClose={closeContextMenu}
      position="bottom-start"
      withArrow
      withinPortal
      styles={{
        dropdown: {
          position: "fixed",
          left: `${contextMenu.x}px`,
          top: `${contextMenu.y}px`,
          zIndex: 50,
          width: 300,
        },
      }}
    >
      <Menu.Dropdown>
        <Menu.Label>
          {(selectedDots && Object.keys(selectedDots).length !== 0) ||
          (selectedTextFigures &&
            Object.keys(selectedTextFigures).length !== 0) ||
          (selectedFanZones && Object.keys(selectedFanZones).length > 0) ? (
            <Title order={5}>
              {t("scheme.redactor.contextMenu.labels.elementsSelected")}
            </Title>
          ) : (
            <Text>
              {t("scheme.redactor.contextMenu.labels.elementsNotSelected")}
            </Text>
          )}
        </Menu.Label>

        {selectedTextFigures.length > 0 && (
          <>
            <Menu.Item onClick={handleForeground}>
              {t("scheme.redactor.contextMenu.labels.foreground")}
            </Menu.Item>
            <Menu.Item onClick={handleBackground}>
              {t("scheme.redactor.contextMenu.labels.background")}
            </Menu.Item>
            <Menu.Divider />
          </>
        )}

        {((selectedDots && Object.keys(selectedDots).length > 0) ||
          (selectedTextFigures &&
            Object.keys(selectedTextFigures).length > 0) ||
          (selectedFanZones && Object.keys(selectedFanZones).length > 0)) && (
          <>
            <Menu.Item onClick={deSelectAll}>
              {t("scheme.redactor.contextMenu.buttons.deSelectDots")}
            </Menu.Item>

            <Menu.Item
              onClick={handleDeleteSelectedElements}
              color="red"
              leftSection={<TrashIcon size={20} />}
            >
              {t("scheme.redactor.contextMenu.buttons.deleteSelectElements")}
            </Menu.Item>
          </>
        )}
      </Menu.Dropdown>
    </Menu>
  );
};

export default React.memo(ContextMenu);
